import { useEffect, useRef } from 'react';

const PRAH_TAHU = 72;

/**
 * Mobil: měsíc se posouvá dotažením seznamu za okraj.
 *
 * Šipky a přepínač období na mobilu nejsou. Kdo dojede na začátek měsíce
 * a táhne dál, dostane předchozí; na konci a dál nahoru další — jako
 * pull-to-refresh, jen místo obnovení přijde sousední měsíc. Stačí dotáhnout
 * nad práh a pustit. Průběh tahu se kreslí přímo do DOM: stav by při každém
 * pohybu prstu překreslil celou stránku.
 *
 * Stejné chování má seznam dnů v Plánu (SmenyModul — tam zatím vlastní kopie).
 *
 * ```jsx
 * const tah = useTahMesicem({ mesic, posun: (s) => s < 0 ? prev() : next(), aktivni: isMobile, ... });
 * <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
 *     {tah.ukazatele}
 *     <div {...tah.seznamProps} style={{ flex: 1, minHeight: 0, overflowY: 'auto', ...tah.seznamStyl }}>
 *         <div ref={tah.obsahRef}>…</div>
 *     </div>
 * </div>
 * ```
 *
 * @param {string} mesic - 'YYYY-MM'
 * @param {(smer: -1|1) => void} posun
 * @param {boolean} aktivni - jen na mobilu
 * @param {string} barva - barva hotového tahu („Pusť pro …")
 * @param {string} mutedColor
 * @param {Array} obnova - data, po jejichž dotažení se znovu odroluje na začátek/konec
 */
export default function useTahMesicem({ mesic, posun, aktivni = true, barva, mutedColor, obnova = [] }) {
    const seznamRef = useRef(null);
    const obsahRef = useRef(null);
    const nahoreRef = useRef(null);
    const doleRef = useRef(null);
    const tahRef = useRef(null);      // { y, nahore, dole, smer, delka } od začátku dotyku
    const cilRef = useRef(null);      // { kam: 'zacatek' | 'konec', do: ms } — kam odrolovat nový měsíc

    const sousedniMesic = (smer) => {
        const [rok, mes] = mesic.split('-').map(Number);
        return new Date(rok, mes - 1 + smer, 1).toLocaleDateString('cs', { month: 'long', year: 'numeric' });
    };

    const ukazTah = (smer, delka) => {
        const obsah = obsahRef.current;
        if (obsah) {
            obsah.style.transition = delka ? 'none' : 'transform .2s ease';
            obsah.style.transform = delka ? `translateY(${smer < 0 ? delka : -delka}px)` : '';
        }
        [[nahoreRef, -1], [doleRef, 1]].forEach(([ref, s]) => {
            const el = ref.current;
            if (!el) return;
            const zapnuty = s === smer && delka > 0;
            el.style.height = zapnuty ? `${delka}px` : '0px';
            if (!zapnuty) return;
            const hotovo = delka >= PRAH_TAHU;
            el.textContent = `${hotovo ? 'Pusť pro' : 'Táhni pro'} ${el.dataset.mesic}`;
            el.style.color = hotovo ? barva : mutedColor;
        });
    };

    const zacatek = (e) => {
        const el = seznamRef.current;
        if (!el) return;
        cilRef.current = null; // uživatel sahá sám — nový měsíc už mu scroll nepřebírá
        tahRef.current = {
            y: e.touches[0].clientY,
            nahore: el.scrollTop <= 0,
            dole: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
            smer: 0, delka: 0,
        };
    };
    const pohyb = (e) => {
        const t = tahRef.current, el = seznamRef.current;
        if (!t || !el) return;
        const dy = e.touches[0].clientY - t.y;
        // Okraj se hlídá i během tahu: kdo začal nahoře, odroloval dolů a pak
        // se vrací, nesmí tahat měsíc, dokud seznam znovu nestojí na začátku.
        if (dy > 0 && t.nahore && el.scrollTop <= 0) { t.smer = -1; t.delka = Math.min(dy * 0.5, 110); }
        else if (dy < 0 && t.dole && el.scrollTop + el.clientHeight >= el.scrollHeight - 1) { t.smer = 1; t.delka = Math.min(-dy * 0.5, 110); }
        else { t.smer = 0; t.delka = 0; }
        ukazTah(t.smer, t.delka);
    };
    const konec = () => {
        const t = tahRef.current;
        tahRef.current = null;
        ukazTah(0, 0);
        if (!t || !t.smer || t.delka < PRAH_TAHU) return;
        // Do předchozího měsíce se přijíždí odspodu — navazuje na to, odkud
        // člověk táhl; do dalšího od začátku.
        cilRef.current = { kam: t.smer < 0 ? 'konec' : 'zacatek', do: Date.now() + 3000 };
        posun(t.smer);
    };

    // Nový měsíc se dotahuje ze serveru, takže „na konec" se roluje znovu,
    // kdykoli dorazí data — ale jen chvíli po tahu a jen dokud se člověk nedotkne.
    useEffect(() => {
        const cil = cilRef.current, el = seznamRef.current;
        if (!cil || !el) return;
        if (Date.now() > cil.do) { cilRef.current = null; return; }
        el.scrollTop = cil.kam === 'konec' ? el.scrollHeight : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mesic, ...obnova]);

    if (!aktivni) {
        return { seznamProps: {}, seznamStyl: null, obsahRef: undefined, ukazatele: null, sousedniMesic };
    }

    const ukazatele = [-1, 1].map(smer => (
        <div key={smer} ref={smer < 0 ? nahoreRef : doleRef} aria-hidden="true"
            data-mesic={sousedniMesic(smer)}
            style={{ position: 'absolute', left: 0, right: 0, zIndex: 1, height: 0, overflow: 'hidden',
                ...(smer < 0 ? { top: 0 } : { bottom: 0 }),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12.5, fontWeight: 600, color: mutedColor, pointerEvents: 'none' }} />
    ));

    return {
        seznamProps: { ref: seznamRef, onTouchStart: zacatek, onTouchMove: pohyb, onTouchEnd: konec, onTouchCancel: konec },
        // Bez vlastního pružení — okraj seznamu patří tahu měsícem.
        seznamStyl: { overscrollBehaviorY: 'none' },
        obsahRef,
        ukazatele,
        sousedniMesic,
    };
}
